import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import ChartLoader from '@/pages/dashboard/_charts/ChartLoader';
import DonutChart from '@/pages/dashboard/_charts/DonutChart';
import colors from 'tailwindcss/colors';
import {useProjectsContext} from "@/pages/dashboard/ProjectsContext";


/**
 * Counts projects by their subjects and returns data suitable for charting.
 *
 * @param {string[]} allSubjects - Array of all possible subject names
 * @param {Array<{subjectNames?: string[]}>} projects - Array of project objects, each with optional subjectNames array
 * @returns {{labels: string[], series: number[]}} Object containing:
 *   - labels: Array of subject names including "No subject selected"
 *   - series: Array of counts corresponding to each label
 */
function countProjectsBySubject(allSubjects, projects) {
  const NO_SUBJECT = "No subject selected";
  const allLabels = [...allSubjects, NO_SUBJECT];

  // Initialize all subjects to 0
  const counts = Object.fromEntries(
    allLabels.map(s => [s, 0])
  );

  // Count references
  for (const {subjectNames = []} of projects) {
    if (!subjectNames || subjectNames.length === 0) {
      counts[NO_SUBJECT]++;
    } else {
      for (const subject of subjectNames || []) {
        if (counts[subject] !== undefined) {
          counts[subject]++;
        }
      }
    }
  }

  return {
    labels: allLabels,
    series: allLabels.map(s => counts[s])
  };
}

export default function ProjectSubjectYear(props) {
  const {subjects, subtitle} = props;
  const {filteredProjects, isLoading} = useProjectsContext();

  const allSubjects = useMemo(() => subjects?.data?.map(s => s?.data?.name) || [], [subjects]);

  const {labels, series}
    = useMemo(() => countProjectsBySubject(allSubjects, filteredProjects || []),
    [filteredProjects, allSubjects])

  if (isLoading) {
    return (
      <ChartLoader
        title="Projects by Subject"
        subtitle={subtitle || 'For school year'}
      />
    );
  }

  const defaultColors = [
    // colors.slate['400'],
    // colors.gray['400'],
    // colors.zinc['400'],
    // colors.neutral['400'],
    // colors.stone['400'],
    colors.red['400'],
    colors.orange['400'],
    // colors.amber['400'],
    colors.yellow['400'],
    colors.lime['400'],
    // colors.green['400'],
    colors.emerald['400'],
    colors.teal['400'],
    colors.cyan['400'],
    colors.sky['400'],
    colors.blue['400'],
    colors.indigo['400'],
    colors.violet['400'],
    // colors.purple['400'],
    colors.fuchsia['400'],
    colors.pink['400'],
    colors.rose['400']
  ];

  return (
    <DonutChart
      title="Projects by Subject"
      subtitle={subtitle || 'For school year'}
      labels={labels}
      series={series}
      colors={defaultColors}
      total={filteredProjects.length}
    />
  );
}

ProjectSubjectYear.propTypes = {
  subjects: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.shape({
      data: PropTypes.shape({
        name: PropTypes.string
      })
    }))
  }),
  subtitle: PropTypes.string
};
